import type { NuxtError } from '#app'
import type { Order } from '@brighterly/lib-core-types/orders/interfaces'
import type { CheckoutResponseData } from '@brighterly/lib-core-types/orders/types'
import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { ScopeMode } from '@shared/lib/scope'

import { useDisciplineStore } from '@entities/customer/@x/order'
import { useToast } from '@shared/lib/toast'
import { useCsRoutes } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { show404 } from '@shared/lib/page-error'
import { entryData, entryMode } from '@shared/lib/scope'

import { buildUpsellPlacementBody, getUpsellOfferUnavailableMessage, resolveUpsellPlacement } from '../lib/upsell-placement'
import { mapCheckoutResponse } from '../lib/checkout-response'
import { useUpsellFlowStore } from '../model/upsell-flow-store/store'
import { useCheckoutStore } from '../model/checkout-store/store'

interface CheckoutApiPayload {
  orderId: number
  route?: RouteLocationNormalizedGeneric
  mode?: ScopeMode
}

interface CheckoutApiOptions {
  onStart?: () => void
  onSuccess?: () => void
  onError?: () => void
  onOfferUnavailable?: (msg: string) => void
  onFinish?: () => void
}

export const useCheckoutApi = async (payload: CheckoutApiPayload, options?: CheckoutApiOptions): Promise<void | NuxtError> => {
  const { $axios } = useNuxtApp()

  const route = payload.route || useRoute()

  const checkoutStore = useCheckoutStore()
  const upsellFlowStore = useUpsellFlowStore()
  const toast = useToast()

  const { orderId, mode } = payload

  const upsellPlacement = resolveUpsellPlacement({
    orderId,
    upsellOrder: entryData(checkoutStore.upsellOrderById, orderId),
    flowPlacement: upsellFlowStore.getPlacement,
  })

  const query = pick({
    ...(route?.query || {}),
    signature: null,
    customer: null,
  })

  const url = useCsRoutes('orders.checkout', {
    '[order_id]': orderId,
    ...query,
  })

  const isBackground = !mode
  const orderMode = mode ?? entryMode(checkoutStore.orderById, orderId, isBackground)

  checkoutStore.setActiveOrderId(orderId)
  const requestId = checkoutStore.beginOrder(orderId, orderMode)

  const set404 = (): NuxtError => {
    checkoutStore.failOrder(orderId, requestId)
    options?.onError?.()
    return show404()
  }

  const seedBillingZipCode = (orderZipCode: Order['zip_code']): void => {
    const alreadyTyped = !!checkoutStore.getBillingZipCode
    if (alreadyTyped || !orderZipCode) return

    checkoutStore.setBillingZipCode(orderZipCode)

    if (checkoutStore.getBillingCountry) {
      checkoutStore.setBillingConfirmed(true)
    }
  }

  if (!url) return set404()
  options?.onStart?.()

  try {
    const { data } = await $axios.post(url, buildUpsellPlacementBody(upsellPlacement))
    if (!data.success) return set404()

    const payload: CheckoutResponseData | null = data?.data?.order ? data.data : null
    if (!payload) return set404()

    const mapped = mapCheckoutResponse(payload)

    const discipline = payload.order.disciplines?.[0]
    if (discipline?.kid?.id && discipline.code) {
      useDisciplineStore().setScope({ kidId: discipline.kid.id, code: discipline.code })
    }

    checkoutStore.settleOrder(orderId, requestId, mapped.order)
    checkoutStore.setPaymentData(mapped.solidgateData)
    checkoutStore.setStripeClientSecret(mapped.stripeClientSecret)
    checkoutStore.setPaypalScriptUrl(mapped.paypalScriptUrl)
    checkoutStore.setAppliesForPromocode(mapped.appliesForPromocode)
    checkoutStore.setFurtherPayments(mapped.furtherPayments)
    seedBillingZipCode(mapped.order.zip_code)

    options?.onSuccess?.()

  } catch (err: any) {
    checkoutStore.failOrder(orderId, requestId)

    const offerUnavailableMessage = options?.onOfferUnavailable ? getUpsellOfferUnavailableMessage(err) : null

    if (offerUnavailableMessage) {
      toast.error(offerUnavailableMessage)
      options?.onOfferUnavailable?.(offerUnavailableMessage)
      return
    }

    const is404 = err?.response?.status === 404
    if (is404) return set404()

    toast.error(err?.response?.data?.message || err?.message || 'Unexpected error')
  } finally {
    options?.onFinish?.()
  }
}
